/*
Created-by: XtroEdge AI
Updated-by: XtroEdge AI
Last modified: 2026-10-15T17:39:19+05:00
Intent: Show only changed attributes in legacy plan-update notification messages.
*/

function comparableValue(value: string): string {
    const text = value.trim().toLowerCase();
    // Compare numeric formatting without losing precision for large limits.
    if (/^[+-]?(?:\d{1,3}(?:,\d{3})+|\d+)(?:\.\d+)?$/.test(text)) {
        const [integer, fraction = ''] = text.replace(/,/g, '').replace(/^\+/, '').split('.');
        const whole = integer.replace(/^(-?)0+(?=\d)/, '$1');
        const decimal = fraction.replace(/0+$/, '');
        return `number:${whole === '-0' && !decimal ? '0' : whole}${decimal ? '.' + decimal : ''}`;
    }
    return text.replace(/[_\s]+/g, ' ');
}

/** The gateway currently sends a prose summary, including unchanged before/after
 * pairs. Keep unfamiliar formats intact rather than guessing at missing values.
 * This is display-only: never rewrite the persisted notification or audit data. */
export function formatPlanUpdateMessage(notification: {
    message?: string;
    notification_name?: string;
    notification_title?: string;
    type?: string;
}): string {
    const message = notification?.message;
    if (typeof message !== 'string') return message ?? '';
    const identity = `${notification.notification_name ?? ''} ${notification.type ?? ''} ${notification.notification_title ?? ''}`;
    if (!(/plan/i.test(identity) && /updat|chang|override/i.test(identity)) &&
        !/administrator changed your .+? plan\./i.test(message)) return message;

    const header = /^(.*?)\b\d+\s+settings?\s+changed\s*[—–-]\s*/is.exec(message);
    if (!header) return message;
    const tail = message.slice(header[0].length);
    const reason = /\.\s*Reason\s*:/i.exec(tail);
    const suffix = reason ? tail.slice(reason.index) : (tail.endsWith('.') ? '.' : '');
    const body = reason ? tail.slice(0, reason.index) : tail.replace(/\.$/, '');
    const entries = body.split(';').map(entry => entry.trim());
    const parsed = entries.map(entry => /^([^:]+):\s*(.*?)\s*(?:→|->)\s*(.*?)$/.exec(entry));
    if (parsed.some(entry => !entry || !entry[2].trim() || !entry[3].trim())) return message;
    const changed = entries.filter((_, index) => {
        const entry = parsed[index]!;
        return comparableValue(entry[2]) !== comparableValue(entry[3]);
    });
    if (!changed.length) {
        return header[1].trimEnd() + (reason ? ' ' + suffix.replace(/^\.\s*/, '') : '');
    }
    return `${header[1]}${changed.length} ${changed.length === 1 ? 'setting' : 'settings'} changed — ${changed.join('; ')}${suffix}`;
}
